/**
 * ARK analytics — GA4 へカスタムイベントを送る最小トランスポート
 *
 * 目的:
 *   ウィッシュリスト保存・車両登録が GA4 に一切残っていないため、地域 (country /
 *   region / city) やチャネル別に「誰が使っているか」を集計できない。engage の DB は
 *   件数しか持たず、地域は Shopify の住所を引かないと分からない。ここで GA に
 *   イベントを流し、標準ディメンションと突き合わせられる状態にする。
 *
 * なぜ gtag.js を読まないか:
 *   GA4 本体は Shopify の Google & YouTube チャネル (web pixels サンドボックス) が
 *   すでに読み込んでいる。テーマ側でもう 1 つ gtag.js を読むと、ストリーム設定の
 *   「拡張計測」がテーマ側インスタンスでも動き、scroll / click / form_start 等が
 *   新たに発生して既存レポートの基準が変わる。ここでは gtag が実際に叩く
 *   /g/collect (Measurement Protocol の web 版) に必要最小のパラメータだけ載せる。
 *
 * ユーザー / セッションの一貫性:
 *   client_id は first-party cookie `_ga`、session_id と通算セッション数は
 *   `_ga_<container>` から読む。つまり Shopify 側が送るイベントと同じユーザー・
 *   同じセッションに紐づく。cookie が無い間は送らずキューに積む (独自の ID を
 *   発番すると GA 上のユーザー数が水増しになる)。
 *
 * 地域が取れる理由:
 *   リクエストは閲覧者のブラウザから直接 google-analytics.com へ出るので、GA は
 *   実 IP から地域を解決する。サーバー経由の Measurement Protocol では地域が付かない。
 *
 * 使い方:
 *   window.ArkAnalytics.track('wishlist_add', { variant_id: '123', logged_in: 'yes' });
 *   値は文字列 (ep.*) か数値 (epn.*) のどちらかに振り分けて送る。
 *
 * イベントパラメータをレポートで使うには GA4 側にカスタムディメンション登録が要る:
 *   ./scripts/dev.sh --project life -- python scripts/infra/ga_client.py \
 *     custom-dim-create 524487079 --parameter vehicle_make --display "Vehicle make" --apply
 */
(function () {
  'use strict';

  var CFG = window.ARK_ANALYTICS || {};
  var MEASUREMENT_ID = typeof CFG.measurementId === 'string' ? CFG.measurementId.trim() : '';
  var ENABLED = CFG.enabled !== false && /^G-[A-Z0-9]{4,20}$/.test(MEASUREMENT_ID);
  var CONTAINER = MEASUREMENT_ID.replace(/^G-/, '');
  var ENDPOINT = 'https://www.google-analytics.com/g/collect';
  var DEBUG = !!CFG.debug;

  // GA4 の上限。超過分は捨てられるので送る前に切る。
  var MAX_NAME_LEN = 40;
  var MAX_VALUE_LEN = 100;
  var MAX_PARAMS = 25;
  // cookie が出るまでの待ち。Shopify のピクセルが _ga を書くのはページ読み込み直後で、
  // 初回訪問の 1 クリック目がそれより早いことがある。
  var COOKIE_RETRY_MS = 400;
  var COOKIE_MAX_RETRY = 15;
  // 送信できないまま溜め続けない (cookie 無効環境で無限に積むのを防ぐ)。
  var MAX_QUEUE = 20;

  var queue = [];
  var retries = 0;
  var hitCount = 0;
  var pageId = String(Math.floor(Math.random() * 2147483647));

  function log() {
    if (!DEBUG || !window.console) return;
    console.log.apply(console, ['[ark-analytics]'].concat([].slice.call(arguments)));
  }

  function readCookie(name) {
    try {
      var m = document.cookie.match(
        '(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'
      );
      return m ? decodeURIComponent(m[1]) : '';
    } catch (_) {
      return '';
    }
  }

  /* _ga=GA1.1.<n>.<n> → client_id は末尾 2 つ。GA1.2.* (別ドメイン粒度) も同形。 */
  function clientId() {
    var m = readCookie('_ga').match(/^GA\d+\.\d+\.(\d+\.\d+)$/);
    return m ? m[1] : '';
  }

  /* セッション cookie は 2 形式ある。どちらも先頭に session_id と通算セッション数。
       GS1.1.<session_id>.<session_number>....
       GS2.1.s<session_id>$o<session_number>$g... */
  function session() {
    var raw = readCookie('_ga_' + CONTAINER);
    if (!raw) return null;
    var m = raw.match(/^GS1\.\d+\.(\d+)\.(\d+)/) || raw.match(/^GS\d+\.\d+\.s(\d+)\$o(\d+)/);
    if (!m) return null;
    return { id: m[1], count: m[2] };
  }

  /* Shopify の同意 API があれば従う。無い環境 (旧ピクセル未適用) では従来通り送る。 */
  function consentGranted() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return true;
      return cp.analyticsProcessingAllowed();
    } catch (_) {
      return true;
    }
  }

  function isEventName(s) {
    return typeof s === 'string' && /^[a-z][a-z0-9_]{0,39}$/.test(s);
  }

  function truncate(s) {
    return s.length > MAX_VALUE_LEN ? s.slice(0, MAX_VALUE_LEN) : s;
  }

  /* params を GA4 の ep.* (文字列) / epn.* (数値) に振り分ける。
     空文字・null・NaN・想定外のキーは落とす (送っても GA 側で捨てられる)。 */
  function encodeParams(params) {
    var out = [];
    if (!params || typeof params !== 'object') return out;
    var keys = Object.keys(params).slice(0, MAX_PARAMS);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      if (!/^[a-z][a-z0-9_]{0,39}$/.test(k) || k.length > MAX_NAME_LEN) continue;
      var v = params[k];
      if (typeof v === 'number' && isFinite(v)) {
        out.push('epn.' + k + '=' + encodeURIComponent(String(v)));
      } else if (typeof v === 'string' && v) {
        out.push('ep.' + k + '=' + encodeURIComponent(truncate(v)));
      }
    }
    return out;
  }

  function send(name, params) {
    var cid = clientId();
    var ses = session();
    if (!cid || !ses) return false;

    hitCount += 1;
    var parts = [
      'v=2',
      'tid=' + encodeURIComponent(MEASUREMENT_ID),
      'cid=' + encodeURIComponent(cid),
      'sid=' + encodeURIComponent(ses.id),
      'sct=' + encodeURIComponent(ses.count),
      'seg=1',
      '_p=' + pageId,
      '_s=' + hitCount,
      'en=' + encodeURIComponent(name),
      'dl=' + encodeURIComponent(location.href),
      'dt=' + encodeURIComponent(truncate(document.title || '')),
      'ul=' + encodeURIComponent((navigator.language || '').toLowerCase()),
      'sr=' + encodeURIComponent(screen.width + 'x' + screen.height),
    ];
    if (document.referrer) parts.push('dr=' + encodeURIComponent(document.referrer));
    parts = parts.concat(encodeParams(params));

    var url = ENDPOINT + '?' + parts.join('&');
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url)) {
        log('sent', name, params);
        return true;
      }
    } catch (_) {
      /* sendBeacon 不可 (サイズ超過等) → fetch にフォールバック */
    }
    try {
      fetch(url, { method: 'POST', keepalive: true, mode: 'no-cors' }).catch(function () {});
      log('sent (fetch)', name, params);
      return true;
    } catch (_) {
      return false;
    }
  }

  /* cookie 待ちのキュー。GA の cookie が出るまでは送れないので、出るまで再試行する。
     上限まで来たら捨てる (cookie 無効・同意拒否のブラウザで溜め続けない)。 */
  function drain() {
    if (!queue.length) return;
    if (!clientId() || !session()) {
      if (retries >= COOKIE_MAX_RETRY) {
        log('give up (no _ga cookie)', queue.length, 'event(s) dropped');
        queue = [];
        return;
      }
      retries += 1;
      setTimeout(drain, COOKIE_RETRY_MS);
      return;
    }
    retries = 0;
    var pending = queue;
    queue = [];
    for (var i = 0; i < pending.length; i++) send(pending[i][0], pending[i][1]);
  }

  function track(name, params) {
    if (!ENABLED) return;
    if (!isEventName(name)) {
      log('invalid event name', name);
      return;
    }
    if (!consentGranted()) {
      log('consent denied', name);
      return;
    }
    if (clientId() && session()) {
      send(name, params);
      return;
    }
    if (queue.length >= MAX_QUEUE) return;
    queue.push([name, params]);
    if (queue.length === 1) setTimeout(drain, COOKIE_RETRY_MS);
  }

  // config snippet が置いたスタブに溜まっている分を引き継ぐ。
  var stub = window.ArkAnalytics;
  window.ArkAnalytics = { track: track, enabled: ENABLED };
  if (stub && stub.q && stub.q.length) {
    for (var i = 0; i < stub.q.length; i++) track(stub.q[i][0], stub.q[i][1]);
  }
  log('ready', { enabled: ENABLED, measurementId: MEASUREMENT_ID });
})();
